/**
 * The journal — one line per chapter, grouped by in-game day, click to jump.
 *
 * **The expensive half of this feature was already built and paid for.** `U2` added a
 * background summarization job and a `summary` column; every chapter in the database has a
 * sentence describing it and, until now, nothing rendered any of them. This is the cheap half.
 *
 * Two details carry more weight than the size of the file suggests:
 *
 * - **The newest chapter has no summary, and that is correct.** The job runs *after* the turn
 * commits, so the chapter a player just finished legitimately has `summary: null` for a few
 * seconds. The story feed is explicit: render the gap, never block on it. So the entry is there, in
 * place, with the line missing — which also means the journal never disagrees with the feed
 * about how many chapters exist. **Opening the drawer is what goes back for the line**
 * (`onOpen`): the client never learns a summary landed, so without that the gap is
 * permanent for the session rather than momentary.
 * - **Jumping is a scroll, not a route.** The chapter is already in the feed (or is not
 * loaded, in which case there is nothing to jump to yet), and `scrollIntoView` on the id
 * `ChapterScene` publishes keeps the reader's place in the story instead of remounting it.
 */
import { useState } from 'react';
import type { Chapter } from '@/api/generated';
import { chapterElementId } from '@/features/story/chapterAnchor';
import { toJournalDays } from '@/features/story/journalDays';
import { useT } from '@/i18n/context';
import { Dialog } from '@/ui/Dialog';

interface JournalProps {
  /** Oldest first. Only what is loaded — the journal shows the story the feed can reach. */
  chapters: readonly Chapter[];
  /**
   * Called when the drawer opens, to go and collect the summaries written since the last
   * fetch. **Without it the journal is empty for the whole of a first session**: every
   * chapter a player writes is put into the cache by `turn/commit.ts` with `summary: null`
   * — correct at that instant, because the job that writes it starts after the turn commits
   * — and nothing refetched the list afterwards, so a story played in one sitting read
   * *"not yet written"* on every line until a reload. The caller owns it because it is the
   * one that knows whether a turn is in flight.
   */
  onOpen?: () => void;
}

/**
 * The toggle is **positioned by its caller** as of the peripheral-surfaces work: `PlayRoute` puts it in a fixed cluster
 * with the link to the story shelf, because two controls that both want the top-right corner
 * cannot each claim it.
 */

export function Journal({ chapters, onOpen }: JournalProps) {
  const t = useT();
  const [open, setOpen] = useState(false);

  const close = (): void => {
    // `Dialog` restores focus to whatever opened it, so the toggle ref this used to keep is
    // gone along with the Escape handler and the backdrop (`ui/Dialog.tsx`).
    setOpen(false);
  };

  const jump = (number: number): void => {
    document.getElementById(chapterElementId(number))?.scrollIntoView({ block: 'start' });
    close();
  };

  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          // Fire before the render, not in an effect: the refetch is the reason the lines
          // are there to read, and the drawer shows whatever it has in the meantime.
          if (!open) onOpen?.();
          setOpen(!open);
        }}
        className="border-border bg-bg/90 text-fg-muted hover:border-accent hover:text-accent z-20 border px-3 py-1.5 font-mono backdrop-blur transition-colors"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {t('ui.nav.journal')}
      </button>

      <Dialog open={open} onClose={close} title={t('ui.nav.journal')} variant="drawer" side="right">
        {chapters.length === 0 ? (
          <p className="text-fg-faint" style={{ fontSize: 'var(--text-meta)' }}>
            {t('ui.chapter.nothing_yet')}
          </p>
        ) : (
          toJournalDays(chapters).map((day) => (
            <section key={day.day} className="mb-6 last:mb-0">
              <h3
                className="text-fg-faint mb-2 font-mono tracking-wider"
                style={{ fontSize: 'var(--text-meta)' }}
              >
                {t('ui.chapter.day_heading', { day: day.day })}
              </h3>
              <ul>
                {day.chapters.map((chapter) => (
                  <li key={chapter.number}>
                    <JournalEntry chapter={chapter} onJump={jump} />
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </Dialog>
    </>
  );
}

function JournalEntry({ chapter, onJump }: { chapter: Chapter; onJump: (n: number) => void }) {
  const t = useT();

  return (
    <button
      type="button"
      onClick={() => {
        onJump(chapter.number);
      }}
      className="hover:bg-bg group flex w-full gap-3 py-1.5 text-left transition-colors"
      style={{ fontSize: 'var(--text-meta)' }}
    >
      <span className="text-fg-faint group-hover:text-accent shrink-0 font-mono tabular-nums">
        {chapter.number}
      </span>
      {chapter.summary === null ? (
        // The summarization job has not run yet. The gap is the honest render, and it fills
        // itself on the next refetch.
        <span className="text-fg-faint italic">{t('ui.chapter.not_yet_written')}</span>
      ) : (
        <span className="text-fg-muted group-hover:text-fg">{chapter.summary}</span>
      )}
    </button>
  );
}
